import { useState, useCallback, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Copy, Check, Pencil, X, RotateCcw, AlertCircle } from 'lucide-react'
import type { FormattedContext } from '@/types/contextEngine'
import { DOMAIN_LABELS } from '@/types/contextEngine'

// ─── Props ───────────────────────────────────────────────────────────────────

interface ContextPackagePreviewProps {
  package_: FormattedContext
  toolName: string
  onRegenerate?: () => void
}

export function ContextPackagePreview({ package_, toolName, onRegenerate }: ContextPackagePreviewProps) {
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(package_.content)

  const displayContent = isEditing ? editContent : package_.content

  // Reset edit mode when package content changes (e.g., after regeneration)
  useEffect(() => {
    setIsEditing(false)
    setEditContent(package_.content)
  }, [package_.content])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(displayContent)
      setCopied(true)
      setCopyFailed(false)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopyFailed(true)
      setTimeout(() => setCopyFailed(false), 3000)
    }
  }, [displayContent])

  const handleStartEdit = useCallback(() => {
    setEditContent(package_.content)
    setIsEditing(true)
  }, [package_.content])

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
    setEditContent(package_.content)
  }, [package_.content])

  const generatedDate = new Date(package_.metadata.generatedAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="space-y-4"
    >
      {/* Tool-branded header */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-base font-semibold text-text-primary">
          What <span className="text-accent-amber">{toolName}</span> will see:
        </h2>
        <div className="flex items-center gap-2">
          {onRegenerate && (
            <button
              onClick={onRegenerate}
              className="inline-flex items-center gap-1.5 rounded-md bg-bg-elevated px-3 py-1.5 text-xs text-text-secondary transition-colors hover:bg-bg-surface-hover hover:text-text-primary"
            >
              <RotateCcw size={13} />
              Regenerate
            </button>
          )}
          {!isEditing && (
            <button
              onClick={handleStartEdit}
              className="inline-flex items-center gap-1.5 rounded-md bg-bg-elevated px-3 py-1.5 text-xs text-text-secondary transition-colors hover:bg-bg-surface-hover hover:text-text-primary"
            >
              <Pencil size={13} />
              Edit
            </button>
          )}
          {isEditing && (
            <button
              onClick={handleCancelEdit}
              className="inline-flex items-center gap-1.5 rounded-md bg-bg-elevated px-3 py-1.5 text-xs text-text-secondary transition-colors hover:bg-bg-surface-hover hover:text-text-primary"
            >
              <X size={13} />
              Cancel
            </button>
          )}
          <button
            onClick={handleCopy}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
              copyFailed
                ? 'bg-accent-error/20 text-accent-error'
                : 'bg-accent-amber text-bg-deepest hover:bg-accent-amber-hover'
            }`}
          >
            {copyFailed ? <AlertCircle size={13} /> : copied ? <Check size={13} /> : <Copy size={13} />}
            {copyFailed ? 'Copy failed' : copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Content preview */}
      <div className="rounded-lg border border-border bg-bg-deepest">
        {isEditing ? (
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full min-h-[300px] resize-y rounded-lg bg-transparent p-4 font-mono text-sm leading-relaxed text-text-primary outline-none"
          />
        ) : (
          <pre className="max-h-[500px] overflow-y-auto custom-scrollbar p-4 font-mono text-sm leading-relaxed text-text-primary whitespace-pre-wrap">
            {package_.content}
          </pre>
        )}
      </div>

      {/* Metadata bar */}
      <MetadataBar package_={package_} generatedDate={generatedDate} />
    </motion.div>
  )
}

// ─── Metadata bar ────────────────────────────────────────────────────────────

function MetadataBar({ package_, generatedDate }: { package_: FormattedContext; generatedDate: string }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-text-tertiary">
      <span className="font-mono">
        {package_.tokenCount.toLocaleString()} tokens
      </span>
      <span>·</span>
      <span>{package_.metadata.chunksUsed} chunks</span>
      <span>·</span>
      <span className="capitalize">{package_.metadata.strategy} strategy</span>
      <span>·</span>
      <span>{generatedDate}</span>

      {/* Domain pills */}
      <div className="ml-auto flex flex-wrap gap-1">
        {package_.metadata.domainsIncluded.map((d) => (
          <span
            key={d}
            className="rounded-sm bg-bg-surface px-1.5 py-0.5 text-[11px] font-mono text-text-tertiary"
          >
            {DOMAIN_LABELS[d]}
          </span>
        ))}
      </div>

      {/* Warnings */}
      {package_.metadata.warnings.length > 0 && (
        <div className="w-full mt-1">
          {package_.metadata.warnings.map((w, i) => (
            <p key={i} className="text-accent-amber text-xs">{w}</p>
          ))}
        </div>
      )}
    </div>
  )
}
